<script setup lang="ts">
import type { DIVISION } from "core/interfaces";
import type { InputSelectOption } from "shared/interfaces";
import { useField, useForm, useIsFormDirty, useIsFormValid } from "vee-validate";
import * as yup from "yup";

import { getDivisionCategoryOptions } from "~/common/helpers/get-division-category-options";
import { ALERT_STATUS } from "~/common/interfaces/alert-status.enum";
import type { UpdateShippingByIdDto } from "~/common/interfaces/dto/shippings/update-shipping-by-id.dto";
import type { ShippingForm } from "~/common/interfaces/form/shipping-form.interface";
import { useAlertStore } from "~/stores/alert";
import { useModalStore } from "~/stores/modal";
import { useShippingsStore } from "~/stores/shippings";

const { t } = useI18n();
const alertStore = useAlertStore();
const shippingsStore = useShippingsStore();
const { shippingsModalEdit } = useModalStore();
const { errors, handleSubmit } = useForm<ShippingForm>({
	initialValues: {
		firstName: shippingsModalEdit.shipping.firstName,
		lastName: shippingsModalEdit.shipping.lastName,
		phoneNumber: shippingsModalEdit.shipping.phoneNumber,
		divisionCategory: {
			id: shippingsModalEdit.shipping.divisionCategory,
			name: t(`COMMON.${shippingsModalEdit.shipping.divisionCategory}`)
		},
		divisionName: shippingsModalEdit.shipping.divisionName,
		divisionAddress: shippingsModalEdit.shipping.divisionAddress
	},
	validationSchema: yup.object(
		{
			firstName: yup
				.string()
				.required(t("INPUT.firstName.error.required")),
			lastName: yup
				.string()
				.required(t("INPUT.lastName.error.required")),
			phoneNumber: yup
				.string()
				.required(t("INPUT.phoneNumber.error.required")),
			divisionCategory: yup.object({
				id: yup
					.string()
					.required(t("INPUT.divisionCategory.error.required"))
			}),
			divisionName: yup
				.string()
				.required(t("INPUT.divisionName.error.required")),
			divisionAddress: yup
				.string()
				.required(t("INPUT.divisionAddress.error.required"))
		}
	)
});
const isFormDirty = useIsFormDirty();
const isFormValid = useIsFormValid();

const adaptShippingFormToDto = (form: ShippingForm): UpdateShippingByIdDto => {
	const { divisionCategory, ...otherFields } = form;

	return {
		divisionCategory: divisionCategory.id,
		...otherFields
	};
};
const onSubmit = handleSubmit(async (form: ShippingForm) => {
	const dto = adaptShippingFormToDto(form);
	shippingsStore.currentShipping = await shippingsStore.patchById(shippingsModalEdit.shipping.id, dto);

	shippingsModalEdit.closeModal();

	alertStore.addAlert({
		title: t("COMPONENTS.SHIPPINGS.MODAL_EDIT.alert.title"),
		description: t("COMPONENTS.SHIPPINGS.MODAL_EDIT.alert.description",
			{
				firstName: shippingsStore.currentShipping.firstName,
				lastName: shippingsStore.currentShipping.lastName
			}),
		status: ALERT_STATUS.SUCCESS
	});
});

const { value: firstName } = useField<string>("firstName");
const { value: lastName } = useField<string>("lastName");
const { value: phoneNumber } = useField<string>("phoneNumber");
const { value: divisionCategory } = useField<InputSelectOption>("divisionCategory");
useField<DIVISION>("divisionCategory.id");
const { value: divisionName } = useField<string>("divisionName");
const { value: divisionAddress } = useField<string>("divisionAddress");

const divisionCategoryOptions = computed(() => getDivisionCategoryOptions());
</script>

<template>
	<AppModal
		:title="t('COMPONENTS.SHIPPINGS.MODAL_EDIT.title')"
		:close-button-text="t('COMMON.cancel')"
		:submit-button-text="t('COMMON.save')"
		:submit-button-active="isFormValid && isFormDirty"
		@close="shippingsModalEdit.closeModal"
		@submit="onSubmit"
	>
		<div class="flex flex-col gap-y-4">
			<div>
				<span class="font-semibold text-base">
					{{ t('COMMON.generalInfo') }}
				</span>
				<div class="mt-4 flex flex-col gap-y-2">
					<div class="flex gap-x-2">
						<AppInput
							v-model="firstName"
							type="text"
							:title="t('COMMON.firstName')"
							:placeholder="t('INPUT.PLACEHOLDER.firstName')"
							:error="errors.firstName"
							required
						/>
						<AppInput
							v-model="lastName"
							type="text"
							:title="t('COMMON.lastName')"
							:placeholder="t('INPUT.PLACEHOLDER.lastName')"
							:error="errors.lastName"
							required
						/>
					</div>
					<div>
						<AppInput
							v-model="phoneNumber"
							type="tel"
							:title="t('COMMON.phoneNumber')"
							:placeholder="t('INPUT.PLACEHOLDER.phoneNumber')"
							:error="errors.phoneNumber"
							required
						/>
					</div>
					<div class="mt-4 space-y-2">
						<AppInputSelect
							v-model="divisionCategory"
							type="text"
							:title="t('COMMON.divisionCategory')"
							:placeholder="t('INPUT.PLACEHOLDER.selectDivisionCategory')"
							options-primary-key="name"
							:options="divisionCategoryOptions"
							:error="errors['divisionCategory.id']"
							required
						/>
						<AppInput
							v-model="divisionName"
							type="text"
							:title="t('COMMON.divisionName')"
							:placeholder="t('INPUT.PLACEHOLDER.divisionName')"
							:error="errors.divisionName"
							required
						/>
						<AppInput
							v-model="divisionAddress"
							type="text"
							:title="t('COMMON.divisionAddress')"
							:placeholder="t('INPUT.PLACEHOLDER.divisionAddress')"
							:error="errors.divisionAddress"
							required
						/>
					</div>
				</div>
			</div>
		</div>
	</AppModal>
</template>
